import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class CommonPopupGlobalSettingsComponent extends PopupInnerModalComponent<PopupData.Common.GlobalVisualSettings> {
	@service('account') declare accountService: Service.Account;
	@service('request') declare requestService: Service.Request;

	@tracked overrideExisting = false;
	@tracked defaultForClients = false;
	@tracked overrideExistingForClients = false;

	get model() {
		return this.popupData.model;
	}

	get isReportSlide() {
		return this.model.modelName === ModelName.REPORT_SLIDE;
	}

	get modelName() {
		return this.isReportSlide ? 'report slides' : 'dashboards';
	}

	@action
	changeOverrideExisting() {
		this.overrideExisting = !this.overrideExisting;
	}

	@action
	changeDefaultForClients() {
		this.defaultForClients = !this.defaultForClients;
	}

	@action
	changeOverrideExistingForClients() {
		this.overrideExistingForClients = !this.overrideExistingForClients;
	}

	@action
	saveAsDefault() {
		const settings = {
			overrideExisting: this.overrideExisting,
			defaultForClients: this.defaultForClients,
			overrideExistingForClients: this.overrideExistingForClients,
		};

		this.accountService
			.saveGlobalSettings(this.model, settings)
			.then(() => {
				this.close();
				this.popupData.onSave();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="InfoFill" @size="lg" />
			Apply these visual settings as default for new
			{{this.modelName}}?
		</div>
		<div class="content">
			<div class="row">
				<p>Apply additional settings:</p>
			</div>
			<div class="row">
				<FormCheckboxComponent
					@selected={{this.overrideExisting}}
					@label="Override existing {{this.modelName}} and account templates"
					@onChange={{this.changeOverrideExisting}}
				/>
			</div>
			{{#if this.accountService.isAgency}}
				<div class="row">
					<FormCheckboxComponent
						@selected={{this.defaultForClients}}
						@label="Set as default for all accounts"
						@onChange={{this.changeDefaultForClients}}
					/>
				</div>
				<div class="row">
					<FormCheckboxComponent
						@selected={{this.overrideExistingForClients}}
						@label="Override existing {{this.modelName}} and account templates in accounts"
						@onChange={{this.changeOverrideExistingForClients}}
					/>
				</div>
			{{/if}}
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Set as default"
						@onTrigger={{this.saveAsDefault}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		</div>
	</template>
}
